import { Component, DestroyRef, ElementRef, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, map, startWith, switchMap, tap } from 'rxjs/operators';
import { PortalStateService } from '../../services/portal-state.service';
import { SolutionsService } from 'src/app/services/api/solutions.service';
import { BlueprintsService } from 'src/app/services/api/blueprints.service';
import { InstancesService } from 'src/app/services/api/instances.service';
import { ConfigurationsService } from 'src/app/services/api/configurations.service';
import { Blueprint } from 'src/app/models/blueprints/blueprint';
import { Configuration } from 'src/app/models/configurations/configuration';
import { PathToLiveConfiguration } from 'src/app/models/blueprints/path-to-live-configuration';
import { Instance } from 'src/app/models/instance/instance';
import { SolutionVersion } from 'src/app/models/service/solution-version';
import { DeploymentClass } from 'src/app/models/instance-class';
import { PathToLiveStageStatus, resolveLifecycleStatus } from '../environment-details/environment-details.component';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';
import { Constants } from 'src/app/models/constants';


export interface LifecycleStatusInfo {
  label: string;
  colorClass: string;
  statusClass: string;
}

export interface PathToLiveStage {
  envId: string;
  environment: string;
  deploymentClass: string;
  version?: string;
  releasePackage?: string;
  lifecycleState?: string;
  blueprintName?: string; 
  hasInstance: boolean;
  status?: PathToLiveStageStatus
  dataZone?: string;
}

export interface ServiceDefinitionView {
  name: string;
  solutionName: string;
  hasInstances: boolean;
  developmentStages: PathToLiveStage[];
  pathToLiveStages: PathToLiveStage[];
}

export interface ServiceDefinitionViewState {
  status: 'loading' | 'ready' | 'error';
  service?: ServiceDefinitionView;
}

@Component({
  selector: 'app-service-definition-details',
  templateUrl: './service-definition-details.component.html',
  styleUrls: ['./service-definition-details.component.scss'],
  standalone: false
})
export class ServiceDefinitionDetailsComponent implements OnInit, OnChanges {
  @Input() serviceId?: string;
  @Input() blueprintId?: string;

  viewState$: Observable<ServiceDefinitionViewState> = of({ status: 'loading' });

  readonly lifecycleStatus: { [lifecycleState: string]: LifecycleStatusInfo } = {
    success: { label: 'FEATURES.SERVICE_DEFINITION_DETAILS.ACTIVE', colorClass: 'green-1-bg', statusClass: 'status-active' },
    failed: { label: 'FEATURES.SERVICE_DEFINITION_DETAILS.FAILED', colorClass: 'warn-solid-bg', statusClass: 'status-failed' }
  };

  private destroyRef = inject(DestroyRef);

  constructor(private portalStateService: PortalStateService, private solutionsService: SolutionsService,
    private blueprintsService: BlueprintsService, private instancesService: InstancesService,
    private configurationsService: ConfigurationsService, private elementRef: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    // app-layout is the scroll container and keeps its offset when the view changes
    this.elementRef.nativeElement.closest('app-layout')?.scrollTo(0, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.serviceId || changes.blueprintId) && this.serviceId) {
      this.viewState$ = this.loadServiceDefinition(this.serviceId, this.blueprintId).pipe(
        map(service => ({ status: 'ready', service }) as ServiceDefinitionViewState),
        startWith({ status: 'loading' } as ServiceDefinitionViewState),
        catchError(() => of({ status: 'error' } as ServiceDefinitionViewState)),
        takeUntilDestroyed(this.destroyRef)
      );
    }
  }

  navigateToEnvironment(stage: PathToLiveStage): void {
    this.portalStateService.viewEnvironment(stage.envId, stage.environment, stage);
  }

  private loadServiceDefinition(serviceId: string, selectedBlueprintId?: string): Observable<ServiceDefinitionView> {
    return combineLatest([
      this.solutionsService.getSolutionDetailsById(serviceId),
      this.blueprintsService.getBlueprintsForSolution(serviceId),
      this.solutionsService.getSolutionVersions(serviceId)
    ]).pipe(
      switchMap(([service, blueprints, versions]) => {
        const blueprint = this.findServiceBlueprint(blueprints, selectedBlueprintId);
        const blueprintId = blueprint?.id;
        const instances$ = blueprintId ? this.instancesService.getInstancesForBlueprint(blueprintId) : of([] as Instance[]);
        const configurations$ = blueprintId ? this.configurationsService.getConfigurations(blueprintId) : of([] as Configuration[]);
        return combineLatest([instances$, configurations$]).pipe(map(([fetchedInstances, configurations]) => {
          const instances = fetchedInstances
            .filter(instance => !Constants.EXCLUDED_INSTANCE_TYPES.includes(instance.type ?? ''))
            .map(instance => ({
            ...instance,
            activeConfiguration: configurations.find(configuration => configuration.id === instance.configurationId)
          }));
          const { developmentStages, pathToLiveStages } = this.buildStages(blueprint?.pathToLiveConfiguration ?? [], instances, versions);
          return {
            name: blueprint?.name ?? service.name,
            solutionName: service.name,
            // "No records found" is shown when nothing is left to list (e.g. the only dev instance is terminated)
            hasInstances: developmentStages.length + pathToLiveStages.length > 0,
            developmentStages: developmentStages.map(stage => ({ ...stage, blueprint })),
            pathToLiveStages: pathToLiveStages.map(stage => ({ ...stage, blueprint }))
          };
        }));
      }),
      tap(service => this.portalStateService.setServiceName(serviceId, service.name))
    );
  }

  private findServiceBlueprint(blueprints: Blueprint[], selectedBlueprintId?: string): Blueprint | undefined {
    return blueprints.find(blueprint => blueprint.id === selectedBlueprintId)
      ?? blueprints.find(blueprint => !blueprint.demo) ?? blueprints[0];
  }

  /**
   * The Path to Live stages come from the blueprint configuration, in promotion order. The development
   * stages are the deployment class D instances from the instances API that are not part of that configuration.
   * Terminated instances are not listed and don't count for the Path to Live check below.
   */
  private buildStages(pathToLive: PathToLiveConfiguration[], instances: Instance[], versions: SolutionVersion[]): { developmentStages: PathToLiveStage[], pathToLiveStages: PathToLiveStage[] } {
    const pathToLiveStages = pathToLive.filter(configuration => !this.isTerminated(instances.find(item => item.id === configuration.instanceId))).map(configuration => {
      const instance = instances.find(item => item.id === configuration.instanceId);
      return this.toStage(configuration.environment, configuration.deploymentClass, instance, versions, configuration.instanceId, configuration.dataZone);
    });
    const developmentStages = instances
      .filter(instance => instance.deploymentClass === DeploymentClass.D)
      .filter(instance => !pathToLive.some(configuration => configuration.instanceId === instance.id))
      .filter(instance => !this.isTerminated(instance))
      .map(instance => this.toStage(instance.name ?? instance.environment ?? '', DeploymentClass.D, instance, versions));
    // Path to Live is only shown when there is a non-terminated class D instance of type "dev" (any other status)
    const hasDevInstance = instances.some(instance =>
      instance.deploymentClass === DeploymentClass.D && instance.type === Constants.DEV_INSTANCE_TYPE && !this.isTerminated(instance));
    return { developmentStages, pathToLiveStages: hasDevInstance ? pathToLiveStages : [] };
  }

  private toStage(environment: string, deploymentClass: string, instance: Instance | undefined, versions: SolutionVersion[], instanceId?: string, dataZone?: string): PathToLiveStage {
  const version = instance ? versions.find(item => item.id === instance.solutionVersionId)?.version : undefined;
  return {
    envId: instance?.id ?? instanceId ?? environment,
    environment,
    deploymentClass,
    version,
    releasePackage: instance?.activeConfiguration?.manifest?.version,
    status: resolveLifecycleStatus(instance?.lifecycleState),
    lifecycleState: instance?.lifecycleState,
    dataZone,
    hasInstance: !!instance
  };
  }

  private isTerminated(instance?: Instance): boolean {
    return instance?.status === DeployStatus.TERMINATED;
  }
}
